/* eslint-disable jsx-a11y/alt-text */
import React from 'react';

import {
  NODE_TYPE as NT,
  RENDER_MODE as RM,
} from 'shared/constants/slate';
import { BASE_COLOR as BC } from 'shared/constants/colors';
import { classes as cn } from 'shared/styles';
import {
  noop,
  isUrl as isUrlCheck,
  isImage as isImageCheck,
} from 'shared/helpers';
import { cx, styled } from 'helpers';
import { DropzoneButton } from 'components/elems';

import {
  highlightWhenSelected,
  MessageFrame,
  ActionFrame,
  MediaLoading,
  FetchErrored,
} from './extra';
import { onFileLoadInsertImageSrc } from '../plugins';
import {
  makeNodeProps,
  handleOrphanReceiverNode,
  transferWithSlateNodeKey,
} from '../utils';



const Img = styled('img')(highlightWhenSelected);


class ImageReceiver extends React.Component {
  constructor(props) {
    super(props);
    this.handleDrop = this.handleDrop.bind(this);
  }

  handleDrop = (accepted) => {
    // DropzoneButton is set with multipart = false,
    // so expect only one image.
    const [ file ] = accepted;
    if (!file) {
      return alert('Invalid file, please try a different image.');
    }

    this.props.onDrop(file);
  }

  render() {
    const { attributes: attrs, children } = this.props;

    return (
      <div className="relative ph2 bg-paper-white" {...attrs}>
        {children}

        <MessageFrame className="pointer-events-none">
          Paste a link to any image from the web..
        </MessageFrame>

        <ActionFrame
          style={{ border: '1px dashed', borderColor: BC.moonGray }}
        >
          <DropzoneButton
            label="or upload file"
            className={cx( "hover-dark-gray", cn.animateFadeInSlow )}
            accept="image/*"
            multiple={false}
            onDrop={this.handleDrop}
          />
        </ActionFrame>
      </div>
    );
  }
}


class ImageTile extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isComplete: false,
    };

    this.onLoad = this.onLoad.bind(this);
  }

  componentDidMount() {
    if (this.image) {
      this.setState({ isComplete: this.image.complete });
    }
  }

  onLoad = () => {
    const { onLoad = noop } = this.props;

    // Callback to adjust shadow caret position after
    // img loads, b/c the element dimension may change
    // pushing selection position but without changing slate
    // doc so editor doesn't re-render. Manually trigger to update.
    this.setState({ isComplete: true }, onLoad);
  }

  render() {
    const { src, isSelected, attributes, onError } = this.props;
    const key = attributes['data-key'];
    const { isComplete } = this.state;

    // crossOrigin: Anonymous to fail fast if not allowed.
    return (
      <figure
        className="relative ma0"
        {...attributes}
      >
        <Img
          crossOrigin="Anonymous"
          innerRef={ref => this.image = ref}
          src={src}
          className={cx('center', isComplete ? 'flex' : 'dn')}
          isSelected={isSelected}
          onDragStart={transferWithSlateNodeKey(key)}
          onError={onError}
          onLoad={this.onLoad}
        />

      {!isComplete && (
        // display: none will still cause issue for dom-to-image,
        // so don't render at all
        <MediaLoading isSelected={isSelected} />
      )}
      </figure>
    );
  }
};


// TODO: Image menu for later..
// {isSelected && (
//   <div
//     contentEditable={false}
//     className={cx(
//       "-abs-fill flex items-center",
//       "bg-red",
//       "pointer-events-none",
//     )}
//   />
// )}


export class Image extends React.Component {
  constructor(props) {
    super(props);

    const { data } = props.node;

    this.state = {
      src: data.get('src', null),
      isReceiving: data.get('isReceiving', false),
      error: data.get('error', null),
    };

    this.loadFileAsDataUrl = this.loadFileAsDataUrl.bind(this);
    this.handleOnError = this.handleOnError.bind(this);
  }

  // XXX: Probably 'file' no longer used..
  // and below is unnecessary now?
  componentDidMount() {
    const { data } = this.props.node;
    const file = data.get('file');
    if (file) {
      this.loadFileAsDataUrl(file);
    }
  }

  componentWillReceiveProps({ node, editor }) {
    // Sync component state with doc node state
    const src = node.data.get('src', null);
    const isReceiving = node.data.get('isReceiving');
    const error = node.data.get('error');
    this.setState({ src, isReceiving, error });

    handleOrphanReceiverNode(node, editor)
  }

  // TODO(tyu): Probably don't use data url, and just
  // immediately upload to cloud somewhere..?
  loadFileAsDataUrl = (file) => {
    const { editor, node } = this.props;

    const reader = new FileReader();
    reader.addEventListener('load',
      () => onFileLoadInsertImageSrc(reader.result, editor, node)
    );
    reader.readAsDataURL(file);
  }

  handleOnError = () => {
    const nodeProps = makeNodeProps({
      type: NT.image,
      isVoid: true,
    }, {
      // Keep src around, so we can guess what went wrong
      src: this.state.src,
      isReceiving: false,
      error: true,
    });

    const { editor, node } = this.props;
    const { value } = editor;
    const change = value.change();

    change.setNodeByKey(node.key, nodeProps);
    return editor.onChange(change);
  }

  // Unfortunately event object from onError does not
  // give you the reason it errored. So we try some
  // educated guessing for what might have gone wrong..
  renderErrorMessage = () => {
    const { src } = this.state

    const isUrl = isUrlCheck(src);
    const isImage = isImageCheck(src);

    if (isUrl && !isImage) return "Invalid link, unable to detect an image.";
    if (isUrl && isImage) return "Permission denied, try uploading directly.";
    return "Error, unable to load the image.";
  }

  render() {
    const props = this.props;

    const { isReceiving, src, error } = this.state;
    if (isReceiving) {
      return <ImageReceiver {...props} onDrop={this.loadFileAsDataUrl} />;
    }

    // Probably bad url.. *ahead* of !src check
    if (error) {
      const message = this.renderErrorMessage()
      return <FetchErrored message={message} {...props} />;
    }

    // Shouldn't be happening anymore, but..
    if (!src) return null;

    const { attributes, isSelected, editor, renderMode } = props;
    const inEditMode = renderMode === RM.RENDER_FOR_EDIT;
    const { etc } = editor.props;

    return (
      <ImageTile
        src={src}
        isSelected={isSelected && inEditMode}
        attributes={attributes}
        onError={this.handleOnError}
        onLoad={etc.updateCaretShadowPosition}
        editor={editor}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/slate/nodes/image.js